import "@angular/common/locales/global/sv";
import { APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule} from "@angular/core";
import { AppComponent } from "./app.component";
import { BrowserModule } from "@angular/platform-browser";
import { AppRoutingModule } from "./app.routes";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from "@angular/common/http";
import { CommonModule } from "@angular/common";
import { StoreModule } from "@ngrx/store";
import { homeReducer } from "./store/home/home.reducer";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { environment } from "../environments/environment";
import { EffectsModule } from "@ngrx/effects";
import { HomeEffects } from "./store/home/home.effects";
import { homeFeatureKey } from "./store/home/home.selector";
import { HomeModule } from "./home/home.module";
import { MSAL_INSTANCE, MSAL_GUARD_CONFIG, MSAL_INTERCEPTOR_CONFIG, MsalService, MsalGuard, MsalBroadcastService, MsalGuardConfiguration, MsalInterceptorConfiguration } from "@azure/msal-angular";
import { BrowserCacheLocation, IPublicClientApplication, InteractionType, PublicClientApplication } from "@azure/msal-browser";
import { AuthInterceptor } from "./interceptors/AuthInterceptor";
import { AdminModule } from "./admin/admin.module";
import { RouterModule } from "@angular/router";
import { AdminEffects } from "./store/admin/admin.effects";
import { adminFeatureKey } from "./store/admin/admin.selector";
import { adminReducer } from "./store/admin/admin.reducer";
import { createOrEditReducer } from "./store/createoredit/createoredit.reducer";
import { createOrEditFeatureKey } from "./store/createoredit/createoredit.selector";
import { CreateOrEditEffects } from "./store/createoredit/createoredit.effects";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatCardModule } from "@angular/material/card";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { MatButtonModule } from "@angular/material/button";
import { provideNativeDateAdapter } from "@angular/material/core";
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';

import { CreateOrUpdateExceptionEffects } from "./store/createorupdateexception/createorupdateexception.effects";
import { GlobalErrorHandler } from "./service/global-error-handler.service";

const isIE =
  window.navigator.userAgent.indexOf("MSIE ") > -1 ||
  window.navigator.userAgent.indexOf("Trident/") > -1;

const scopeUserRead: string = "user.read";
export const protectedResourceMap: Map<string, Array<string>> = new Map<string, Array<string>>([
  [environment.LineMapBaseUrl, [environment.lineMapScopes]],
  [environment.MessagesAPiBaseUrl, [environment.messagesScopes]],
  ["https://graph.microsoft.com/v1.0", [scopeUserRead]],
]);

// Factory function to create and initialize MSAL instance
export function MSALInstanceFactory(): IPublicClientApplication {
  const msalInstance = new PublicClientApplication({
    auth: {
      clientId: environment.clientID,
      authority: environment.loginUrl + environment.tenantID,
      redirectUri: environment.loginRedirect,
      postLogoutRedirectUri: environment.url,
      navigateToLoginRequestUrl: true
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE
    }
  });
  
  return msalInstance;
}

// Initialize MSAL before app starts
export function initializeMSAL(msalInstance: IPublicClientApplication): () => Promise<void> {
  return () => {
    return msalInstance.initialize().then(() => {
      console.log('MSAL initialized successfully');
    }).catch((error) => {
      console.error('MSAL initialization failed:', error);
      throw error;
    });
  };
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    RouterModule,
    CommonModule,
    HomeModule,
    AdminModule,
    MatCardModule,
    MatDatepickerModule,
    MatSnackBarModule,
    MatButtonModule,
    NgbModule,
    StoreModule.forRoot({}),
    StoreModule.forFeature(homeFeatureKey, homeReducer),
    StoreModule.forFeature(adminFeatureKey, adminReducer),
    StoreModule.forFeature(createOrEditFeatureKey, createOrEditReducer),
    StoreDevtoolsModule.instrument({maxAge: 25, logOnly: environment.production}),
    EffectsModule.forRoot(HomeEffects, AdminEffects, CreateOrEditEffects, CreateOrUpdateExceptionEffects)
  ],
  providers: [
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeMSAL,
      deps: [MSAL_INSTANCE],
      multi: true
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    {
      provide: MSAL_GUARD_CONFIG,
      useValue: {
        interactionType: InteractionType.Redirect,
        authRequest: {
          scopes: [scopeUserRead]
        }
      } as MsalGuardConfiguration
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useValue: {
        interactionType: InteractionType.Redirect,
        protectedResourceMap: protectedResourceMap
      } as MsalInterceptorConfiguration
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    {
      provide: LOCALE_ID,
      useValue: "sv-SE",
    },
    provideNativeDateAdapter(),
    provideAnimationsAsync(),
    provideHttpClient(withXhr(), withInterceptorsFromDi())
  ]
})
export class AppModule {}
